import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// Importăm componentele necesare
import Button from './ui/Button';
import Logo from './Logo/Logo';
import LanguageSwitcher from './LanguageSwitcher/LanguageSwitcher';

// Import SCSS - MODULAR: Navbar își importă propriul SCSS
import './Navbar.scss';

// Constante pentru meniul principal (paths)
const MENU_ITEMS = [
  { key: 'home', path: '/' },
  { key: 'about', path: '/despre-noi' },
  { key: 'process', path: '/procesul' },
];

// Animație pentru meniul mobil
const mobileMenuVariants = {
  hidden: { x: 300 },
  visible: { 
    x: 0,
    transition: { type: 'spring', damping: 25, stiffness: 250 }
  },
  exit: { x: 300 }
};

const Navbar = () => {
  const { t } = useTranslation('common');
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Efect pentru a detecta scroll și direcția
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Schimbă stilul după 50px scroll
      if (currentScrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      // Auto-hide logic (doar pe mobil - sub 1024px)
      if (window.innerWidth < 1024) {
        if (currentScrollY < 10) {
          // La top, mereu vizibil
          setVisible(true);
        } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
          // Scroll DOWN și peste 100px → ascunde
          setVisible(false);
        } else if (currentScrollY < lastScrollY) {
          // Scroll UP → arată
          setVisible(true);
        }
      } else {
        // Pe desktop, mereu vizibil
        setVisible(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Funcție pentru a verifica dacă link-ul este activ
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Închide meniul mobil
  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''} ${!visible ? 'navbar--hidden' : ''}`}>
      <div className={`navbar__logo ${scrolled ? 'navbar__logo--scrolled' : ''}`}>
        <Logo linkTo="/" />
      </div>
      
      <div className="navbar__links">
        {MENU_ITEMS.map((item) => (
          <Link 
            key={item.path} 
            to={item.path} 
            className={`navbar__link ${scrolled ? 'navbar__link--scrolled' : ''} ${isActive(item.path) ? 'active' : ''}`}
          >
            {t(`navbar.${item.key}`)}
          </Link>
        ))}
        <Button 
          to="/contact" 
          type="primary" 
          as="link"
        >
          {t('navbar.contact')}
        </Button>
        <LanguageSwitcher scrolled={scrolled} className="navbar__language" />
      </div>
      
      <div 
        className={`navbar__toggle ${scrolled ? 'navbar__toggle--scrolled' : ''}`} 
        onClick={() => setMobileMenuOpen(true)}
      >
        <FaBars />
      </div>
      
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="mobile-menu"
            variants={mobileMenuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="mobile-menu__header">
              <div className="navbar__logo navbar__logo--scrolled">
                <Logo linkTo="/" />
              </div>
              <div className="mobile-menu__close" onClick={closeMobileMenu}>
                <FaTimes />
              </div>
            </div>
            
            <div className="mobile-menu__links">
              {MENU_ITEMS.map((item) => (
                <Link 
                  key={item.path} 
                  to={item.path} 
                  className={`mobile-menu__link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  {t(`navbar.${item.key}`)}
                </Link>
              ))}
              <Button 
                to="/contact" 
                type="primary" 
                as="link"
                onClick={closeMobileMenu}
                style={{ marginTop: '1rem' }}
              >
                {t('navbar.contact')}
              </Button>
              <LanguageSwitcher scrolled className="mobile-menu__language" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

Navbar.propTypes = {}; // Nu sunt necesare props-uri pentru această componentă

export default Navbar;
